import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  MapPin,
  DollarSign,
  CalendarDays,
  Lock,
  Phone,
  Mail,
  User,
  Users,
  Home,
  ArrowLeft,
  CheckCircle2,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import JobCard, { Listing } from '@/components/JobCard';
import CheckoutModal from '@/components/CheckoutModal';
import LeadPurchaseBar from '@/components/LeadPurchaseBar';
import { MarketplaceDisclaimer } from '@/components/MarketplaceDisclaimer';


import PhotoGallery from '@/components/PhotoGallery';
import {
  Contact,
  claimLead,
  fetchContact,
  fetchFullListing,
  fetchListings,
  fetchPublicListing,
  isSoldOut,
  recordPurchase,
} from '@/lib/marketplace';
import { getLeadBalance, EMPTY_BALANCE, LeadBalance } from '@/lib/leadBalance';
import { canBuyLeads, coverImageFor } from '@/lib/roles';
import { LeadSettlement, clearIntent, readIntent, settleLeadPayment } from '@/lib/singleLead';

import { locationLabel, parseDocuments, safeFileName, ListingDocument } from '@/lib/listingSettings';
import { money, tradeLabel, canonicalSlug, localizedSlug, LEAD_PRICE } from '@/data/constants';





const JobDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, path, lang } = useI18n();
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState<Listing | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [docBusy, setDocBusy] = useState('');
  const [docError, setDocError] = useState('');
  const [similar, setSimilar] = useState<Listing[]>([]);
  const [checkout, setCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState('');
  /** Outcome of a $29 payment: '' | 'credited' | 'unlocked' (legacy orders). */

  const [paidFlash, setPaidFlash] = useState('');
  const [balance, setBalance] = useState<LeadBalance>(EMPTY_BALANCE);


  /**
   * Documents live in a PRIVATE bucket. The server re-checks ownership /
   * purchase / admin before minting a 5-minute signed URL, so copying a path
   * gives an unentitled user nothing.
   */
  const openDocument = async (doc: ListingDocument) => {
    if (!job) return;
    setDocError('');
    setDocBusy(doc.path);
    const { data, error } = await supabase.functions.invoke('listing-media', {
      body: { action: 'sign-download', listingId: job.id, path: doc.path },
    });
    setDocBusy('');
    if (error || !data?.url) {
      setDocError(t('docs.errLocked'));
      return;
    }
    window.open(data.url, '_blank', 'noopener,noreferrer');
  };


  /**
   * Pull the full row (city, zip, documents). RLS on mkt_listings only returns
   * it to the owner, a buyer of this lead, or an admin — so a successful read
   * IS the proof that the viewer is allowed to see the exact location.
   */
  const loadUnlocked = async (listing: Listing) => {
    const full = await fetchFullListing(listing.id);
    if (!full) return false;
    setJob(full);
    setDocuments(parseDocuments(full.documents));
    setContact(await fetchContact(listing.id));
    setUnlocked(true);
    return true;
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setUnlocked(false);
    setContact(null);
    setDocuments([]);

    const load = async () => {
      try {
        const dbSlug = canonicalSlug(slug || '');
        // Always start from the masked public view so anonymous visitors and
        // subcontractors who have not paid still get title/category/city/state.
        const masked = await fetchPublicListing(dbSlug);
        if (!active) return;
        setJob(masked);
        // Leave the loading state the moment the primary record resolves — the
        // balance / entitlement / similar-projects calls below are secondary and
        // must never be able to hold the page on a spinner.
        setLoading(false);

        if (profile?.id) {
          const b = await getLeadBalance(profile.id);
          if (active) setBalance(b);
        }

        if (masked) {
          if (profile?.id) await loadUnlocked(masked);
          const near = await fetchListings({ trade: masked.trade, limit: 4 });
          if (active) setSimilar(near.filter((x) => x.id !== masked.id).slice(0, 3));
        }
      } catch (error) {
        console.error('[JobDetail] Failed to load listing:', error);
        if (!active) return;
        setJob(null);
      } finally {
        // Guarantees the detail page can never be stranded on "Loading…".
        if (active) setLoading(false);
      }
    };
    load();

    return () => {
      active = false;
    };
  }, [slug, profile?.id]);


  /**
   * Unlock the lead.
   *
   * The browser no longer writes the entitlement. `claimLead` calls the
   * SECURITY DEFINER function `mkt_claim_lead`, which re-derives the buyer from
   * the JWT, re-checks role/status/listing, consumes exactly one included lead
   * under a row lock and inserts the purchase — all in one transaction, with a
   * unique index making duplicates impossible. A crafted REST call cannot grant
   * itself access because mkt_lead_purchases has no client INSERT policy.
   */
  const finalizeUnlock = async () => {
    if (!job || !profile) return;
    setFlash('');

    const res = await claimLead(job.id);

    if (!res.ok) {
      setCheckout(false);
      if (res.reason === 'sold_out') {
        // Capacity was reached while this page was open. The database already
        // flipped the listing to sold_out; reflect that immediately.
        setJob((j) => (j ? { ...j, status: 'sold_out', contractor_count: res.slotsTaken } : j));
        setFlash(t('leads.soldOutFlash'));
      } else if (
        res.reason === 'no_credits' ||
        res.reason === 'allowance_exhausted' ||
        res.reason === 'plan_expired'
      ) {
        setBalance((b) => ({
          ...b,
          used: b.included,
          remaining: 0,
          spendable: b.extra,
          exhausted: b.included > 0,
        }));
        setFlash(balance.hasSubscription ? t('leads.noneLeft') : t('pay.leadNeedsPlan'));
      } else if (res.reason === 'no_active_plan') {
        setFlash(t('pay.leadNeedsPlan'));
      } else {
        setFlash(t('jobs.unlockFailed'));
      }
      return;
    }

    setBalance((b) => ({
      ...b,
      used: Math.min(b.included, b.used + 1),
      remaining: res.remaining,
      spendable: Math.max(0, b.spendable - 1),
      exhausted: res.remaining === 0,
    }));
    await refreshProfile();

    // CRM bookkeeping only — the entitlement already exists server-side.
    await recordPurchase({
      listing: job,
      buyerId: profile.id,
      amount: LEAD_PRICE,
      buyerEmail: profile.email,
      buyerName: profile.company_name || profile.contact_name || undefined,
      buyerPhone: profile.phone || undefined,
      lang,
    });

    // The purchase row now satisfies the RLS read policy, so the full record
    // (city, ZIP, documents, contact) becomes readable.
    await loadUnlocked(job);
    setCheckout(false);
    setFlash(res.reason === 'already_owned' ? t('jobs.unlocked') : t('pay.usedIncluded'));
  };
  /**
   * A $29 payment settled.
   *
   * THE PURCHASE BOUGHT A CREDIT, NOT THIS LEAD. The server added exactly one
   * credit to the balance and stopped there — identical to a membership
   * dropping 5, 10 or 20 credits in. Nothing is unlocked until the contractor
   * chooses to spend it, so this handler only refreshes the balance and shows
   * the choice. (`s.unlocked` can only be true for an old order settled under
   * the retired auto-consume rule; it is honoured so history reads correctly.)
   */
  const handleLeadPaid = async (s: LeadSettlement) => {
    clearIntent();
    setCheckout(false);
    await refreshProfile();
    if (profile?.id) setBalance(await getLeadBalance(profile.id));

    if (s.unlocked) {
      await loadUnlocked(job as Listing);
      setPaidFlash('unlocked');
      return;
    }
    // If this project filled up while the card was being charged, the credit is
    // still perfectly good on any other open project — so there is nothing to
    // refund and nothing is lost. Reflect the capacity change and let them choose.
    if (s.slotsMax > 0 && s.slotsTaken >= s.slotsMax) {
      setJob((j) => (j ? { ...j, status: 'sold_out', contractor_count: s.slotsTaken } : j));
    }
    setPaidFlash('credited');
  };


  /**
   * RESUME AN INTERRUPTED PAYMENT. If the customer was interrupted after the
   * card was charged (backgrounded Safari, reload, dead battery), settle the
   * remembered payment on load. Settlement is idempotent server-side, so this
   * can never issue a second credit or a second entitlement.
   */
  useEffect(() => {
    if (!job?.id || !profile?.id || unlocked) return;
    const ticket = readIntent(job.id);
    if (!ticket) return;
    let active = true;
    (async () => {
      const s = await settleLeadPayment(ticket.paymentIntentId, job.id, lang);
      if (!active) return;
      if (s.ok) await handleLeadPaid(s);
      else if (s.error === 'payment_not_succeeded') clearIntent();
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, profile?.id, unlocked]);

  const handleUnlockClick = () => {
    if (!profile) {
      navigate(`${path('/login')}?next=${encodeURIComponent(path(`/jobs/${slug}`))}`);
      return;
    }
    // Only contractor-side accounts may unlock leads. The same rule is enforced
    // server-side inside mkt_claim_lead (role_not_allowed), so hiding the button
    // is a convenience, not the control.
    if (!canBuyLeads(profile.user_type)) {
      setFlash(t('jobs.loginToUnlock'));
      return;
    }
    setCheckout(true);
  };





  if (loading) return <div className="mx-auto max-w-5xl px-4 py-20 text-center text-slate-500">{t('common.loading')}</div>;
  if (!job)
    return (
      <div className="mx-auto max-w-5xl px-4 py-20 text-center">
        <p className="text-slate-600">{t('jobs.notFound')}</p>
        <Link to={path('/jobs')} className="mt-4 inline-block font-bold text-orange-600">
          {t('jobs.title')}
        </Link>
      </div>
    );

  const photos = (job.photos || []).filter(Boolean);
  // Priority: homeowner upload -> trade/category cover -> branded marketplace
  // cover. Never a gray tile, on any project, in any language.
  const cover = coverImageFor(job);
  // LOCATION PRIVACY: city + state ("Fontana, CA") are public on every preview.
  // Street address, ZIP, homeowner name, phone and email stay locked until the
  // lead is purchased — that is what `exactLocationLocked` gates.
  const exactLocationLocked = !unlocked;
  const place = locationLabel({ city: job.city, state: job.state });
  const docCount = unlocked ? documents.length : job.document_count ?? 0;
  // 6 of 6 contractor slots filled. A buyer who already owns the lead keeps
  // full access — only NEW unlocks are closed off.
  const soldOut = !unlocked && isSoldOut(job);



  return (
    /*
      MOBILE BOTTOM PADDING. The sticky purchase bar (below `lg`) is ~84px
      tall plus the safe-area inset, so the page reserves that space and the
      disclaimer / similar projects can never end up underneath it.
    */
    <div className="mx-auto max-w-6xl px-4 py-8 pb-32 sm:px-6 lg:px-8 lg:pb-8">

      <Seo
        title={`${tradeLabel(job.trade, lang)} — ${place}`}
        description={(job.description || job.title).slice(0, 160)}
        schema={{
          '@type': 'JobPosting',
          title: job.title,
          description: job.description || job.title,
          datePosted: job.created_at,
          employmentType: 'CONTRACTOR',
          industry: tradeLabel(job.trade, 'en'),
          inLanguage: lang,
          image: cover.src,

          hiringOrganization: { '@type': 'Organization', name: job.source_type === 'gc' ? 'General Contractor' : 'Homeowner' },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              // Only publish the precise locality once it is public anyway.
              addressRegion: job.state,
              addressCountry: 'US',
            },
          },
        }}
      />

      <button
        onClick={() => navigate(path('/jobs'))}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> {t('jobs.title')}
      </button>

      {/*
        $29 PURCHASE RESULT.

        A purchase adds 1 credit to the balance — it never spends it. So the
        confirmation offers the spend as a CHOICE ("unlock this one now" or
        "keep it and pick another project"), exactly like a membership credit.
        The 'unlocked' banner only ever appears for orders settled under the
        retired auto-consume behaviour, so that history stays truthful.
      */}
      {paidFlash === 'credited' && (
        <div className="mb-6 rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-5 py-4">
          <p className="flex items-center gap-2 text-base font-extrabold text-emerald-900">
            <CheckCircle2 className="h-5 w-5" /> {t('pay.creditedTitle')}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-emerald-800">{t('pay.creditedBody')}</p>
          <p className="mt-2 text-sm font-bold text-emerald-900">
            {t('pay.creditsInBalance')}: {balance.spendable}

          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!unlocked && !soldOut && (
              <button
                type="button"
                onClick={finalizeUnlock}
                style={{ touchAction: 'manipulation' }}
                className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
              >
                {t('pay.unlockNowCta')}
              </button>
            )}
            <Link
              to={path('/jobs')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-emerald-300 px-4 py-2.5 text-sm font-bold text-emerald-800 hover:bg-emerald-100"
            >
              {t('pay.saveCreditCta')}
            </Link>
          </div>
        </div>
      )}
      {paidFlash === 'unlocked' && (
        <div className="mb-6 rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-5 py-4">
          <p className="flex items-center gap-2 text-base font-extrabold text-emerald-900">
            <CheckCircle2 className="h-5 w-5" /> {t('pay.successTitle')}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-emerald-800">{t('pay.successBody')}</p>
        </div>
      )}


      {flash && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> {flash}
        </div>
      )}


      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
              {tradeLabel(job.trade, lang)}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              {job.project_type === 'commercial' ? t('common.commercial') : t('common.residential')}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              {job.source_type === 'gc' ? t('jobs.byGc') : t('jobs.byHomeowner')}
            </span>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-900">{job.title}</h1>

          <PhotoGallery
            photos={photos}
            alt={job.title}
            fallbackCover={cover.isRealPhoto ? undefined : cover.src}
          />


          <dl className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <MapPin className="h-3.5 w-3.5" /> {t('common.location')}
              </dt>
              {/* City + state abbreviation, always. Never the street or ZIP. */}
              <dd className="mt-1 font-bold text-slate-900">{place || '—'}</dd>
              {exactLocationLocked && (
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
                  {t('jobs.cityStateOnly')}
                </dd>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <DollarSign className="h-3.5 w-3.5" /> {t('common.budget')}
              </dt>
              <dd className="mt-1 font-bold text-slate-900">{job.budget_range || '—'}</dd>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <CalendarDays className="h-3.5 w-3.5" /> {job.source_type === 'gc' ? t('jobs.dueDate') : t('jobs.startDate')}
              </dt>
              <dd className="mt-1 font-bold text-slate-900">{job.target_date || '—'}</dd>
            </div>
          </dl>

          {exactLocationLocked && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
                <Lock className="h-4 w-4" /> {t('jobs.locationLockedTitle')}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-amber-800">{t('jobs.locationLockedBody')}</p>
            </div>
          )}


          <h2 className="mt-8 text-lg font-extrabold text-slate-900">{t('jobs.description')}</h2>
          <p className="mt-2 whitespace-pre-line leading-relaxed text-slate-600">{job.description}</p>

          {docCount > 0 && (
            <>
              <h2 className="mt-8 text-lg font-extrabold text-slate-900">{t('jobs.documents')}</h2>
              {unlocked ? (
                <ul className="mt-3 space-y-2">
                  {documents.map((d) => (
                    <li
                      key={d.path}
                      className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <FileText className="h-5 w-5 shrink-0 text-red-500" />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                        {safeFileName(d.name)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                        {t(`docs.kind.${d.kind || 'other'}`)}
                      </span>
                      <button
                        type="button"
                        onClick={() => openDocument(d)}
                        disabled={docBusy === d.path}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-700 disabled:opacity-50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> {docBusy === d.path ? t('common.loading') : t('docs.open')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
                  <Lock className="h-4 w-4 text-slate-400" />
                  {docCount} {t('jobs.documentsLocked')}
                </p>
              )}
              {docError && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{docError}</p>}
            </>
          )}


          {similar.length > 0 && (
            <>
              <h2 className="mt-12 text-lg font-extrabold text-slate-900">{t('jobs.similar')}</h2>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                {similar.map((s) => (
                  <JobCard
                    key={s.id}
                    job={s}
                    onUnlock={() => navigate(path(`/jobs/${localizedSlug(s.slug, s.trade, lang)}`))}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <aside>
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('jobs.leadPrice')}</p>
            <p className="mt-1 text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
            <p className="mt-1 text-xs font-semibold text-slate-400">{t('pricing.fixedLeadNote')}</p>


            {unlocked && contact ? (
              <div className="mt-5 space-y-3 rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">{t('jobs.contactUnlocked')}</p>
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <User className="h-4 w-4 text-emerald-600" />
                  {contact.contact_name}
                </p>
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-700">
                  <Phone className="h-4 w-4 text-emerald-600" />
                  {contact.phone}
                </a>
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 break-all text-sm font-semibold text-slate-800 hover:text-emerald-700"
                >
                  <Mail className="h-4 w-4 text-emerald-600" />
                  {contact.email}
                </a>
                <p className="flex items-start gap-2 text-sm text-slate-700">
                  <Home className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {contact.address}
                </p>
                <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">{t('jobs.unlocksNow')}</p>
              </div>
            ) : soldOut ? (
              /*
                CAPACITY REACHED. A lead is shared with at most 6 contractors;
                the database flipped this listing to sold_out inside the same
                locked transaction that granted the 6th slot, so no further
                unlock is possible and no credit can be deducted. The unlock
                button is replaced (not merely disabled) so there is no path
                that could spend a credit on a full lead.
              */
              <div className="mt-5 rounded-xl border border-slate-300 bg-slate-50 p-4">
                <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-700">
                  <Users className="h-4 w-4" /> {t('leads.soldOutTitle')}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t('leads.soldOutBody')}</p>
                <Link
                  to={path('/jobs')}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800"
                >
                  {t('leads.soldOutCta')}
                </Link>
              </div>
            ) : (
              <>
                <p className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {t('jobs.contactLocked')}
                </p>
                {exactLocationLocked && (
                  <p className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t('jobs.exactLocationHidden')}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleUnlockClick}
                  style={{ touchAction: 'manipulation' }}
                  className="mt-4 min-h-[52px] w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
                >
                  {/*
                    THE ACTION IS ALWAYS EXPLICIT AND HONEST:
                    with a credit  -> "Unlock for 1 Credit"
                    without one    -> "Buy This Lead — $29"
                  */}
                  {balance.spendable > 0 ? t('jobs.unlockWithCredit') : `${t('pay.buyThisLead')} — ${money(LEAD_PRICE)}`}
                </button>
                <p className="mt-2 text-center text-[11px] font-semibold text-slate-400">
                  {/* Buying tops the balance up by one — it does not spend it. */}
                  {balance.spendable > 0 ? t('leads.oneCreditNote') : t('pay.creditNote')}
                </p>

                {!profile && <p className="mt-3 text-center text-xs text-slate-400">{t('jobs.loginToUnlock')}</p>}
              </>
            )}
            <MarketplaceDisclaimer variant="short" role="subcontractor" className="mt-5" />
          </div>
        </aside>

      </div>

      <CheckoutModal
        open={checkout}
        onClose={() => setCheckout(false)}
        amount={LEAD_PRICE}

        title={t('pay.unlockTitle')}
        subtitle={t('pay.unlockSub')}
        lineItem={`${t('pay.leadFor')}: ${tradeLabel(job.trade, lang)} — ${place}`}
        plansHref={path('/pricing')}
        listingId={job.id}
        listingSlug={job.slug}
        onLeadPaid={handleLeadPaid}
        altAction={
          balance.spendable > 0
            ? {
                label: `${t('pay.useIncluded')} (${balance.spendable})`,
                run: () => finalizeUnlock(),
              }
            : undefined
        }

        note={
          balance.spendable > 0
            ? undefined
            : balance.hasSubscription
            ? t('leads.noneLeft')
            : t('pay.leadNeedsPlan')
        }
      />

      {/*
        MOBILE PURCHASE BAR — the fix for the reported "no purchase button on
        mobile" report. Below `lg` the sidebar card is the last block in the
        document, so this pinned bar carries the SAME handler and keeps the
        $29 CTA on screen at every scroll position and every screen width.
        It hides itself when the lead is already owned or sold out, and while
        the checkout modal is open.
      */}
      <LeadPurchaseBar
        unlocked={unlocked}
        soldOut={soldOut}
        credits={balance.spendable}
        onPurchase={handleUnlockClick}
      />
    </div>

  );
};

export default JobDetail;
